import { useId, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

interface FieldFrameProps {
  id: string;
  label: ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
  disabled?: boolean;
  children: ReactNode;
}

/** Label, outlined shell and message shared by every field. */
function FieldFrame({ id, label, required, error, hint, disabled, children }: FieldFrameProps) {
  const invalid = Boolean(error);
  const message = error || hint;
  return (
    <div className="fw-field">
      <label htmlFor={id} className="fw-field__label">
        {label}
        {required ? ' *' : ''}
      </label>
      <div
        className={`fw-field__shell${invalid ? ' fw-field__shell--invalid' : ''}${disabled ? ' fw-field__shell--disabled' : ''}`}
      >
        {children}
      </div>
      {message && (
        <div
          id={`${id}-msg`}
          className={`fw-field__message${invalid ? ' fw-field__message--invalid' : ''}`}
        >
          {invalid && <Icon name="alert-circle" size={16} style={{ marginTop: '2px' }} />}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}

export interface TextFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  inputMode?: 'text' | 'email' | 'tel' | 'search' | 'url';
  autoComplete?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  maxLength?: number;
  autoCapitalize?: string;
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  disabled,
  inputMode,
  autoComplete,
  name,
  id,
  placeholder,
  maxLength,
  autoCapitalize,
}: TextFieldProps) {
  const auto = useId();
  const rid = id ?? `fw-${auto}`;
  return (
    <FieldFrame
      id={rid}
      label={label}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
    >
      <input
        id={rid}
        name={name}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        maxLength={maxLength}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${rid}-msg` : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="fw-field__input"
      />
    </FieldFrame>
  );
}

export interface PasswordFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  autoComplete?: 'current-password' | 'new-password';
  showLabel: string;
  hideLabel: string;
}

export function PasswordField({
  label,
  value,
  onChange,
  hint,
  error,
  disabled,
  name = 'password',
  id,
  autoComplete = 'current-password',
  showLabel,
  hideLabel,
}: PasswordFieldProps) {
  const [shown, setShown] = useState(false);
  const auto = useId();
  const rid = id ?? `fw-${auto}`;
  return (
    <FieldFrame id={rid} label={label} error={error} hint={hint} disabled={disabled}>
      <input
        id={rid}
        name={name}
        type={shown ? 'text' : 'password'}
        value={value}
        autoComplete={autoComplete}
        spellCheck={false}
        autoCapitalize="none"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`fw-field__input${shown ? '' : ' fw-field__input--password'}`}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? hideLabel : showLabel}
        aria-pressed={shown}
        className="fw-field__toggle"
      >
        <Icon name={shown ? 'eye-off-outline' : 'eye-outline'} size={22} />
      </button>
    </FieldFrame>
  );
}

export interface NumericFieldProps {
  label: ReactNode;
  /** Empty string when blank. */
  value: string;
  onChange: (value: string) => void;
  unit?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  name?: string;
  id?: string;
}

export function NumericField({
  label,
  value,
  onChange,
  unit,
  hint,
  error,
  required,
  disabled,
  min,
  max,
  name,
  id,
}: NumericFieldProps) {
  const auto = useId();
  const rid = id ?? `fw-${auto}`;
  return (
    <FieldFrame
      id={rid}
      label={label}
      required={required}
      error={error}
      hint={hint}
      disabled={disabled}
    >
      <input
        id={rid}
        name={name}
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
        className="fw-field__input fw-field__input--numeric fw-tnum"
      />
      {unit && <span className="fw-field__unit">{unit}</span>}
    </FieldFrame>
  );
}
